import {type ReactNode, useCallback, useMemo, useRef, useId, useEffect} from 'react'
import {type Direction, FocusKeys} from '@primer/behaviors'
import {ChevronLeftIcon, ChevronRightIcon} from '@primer/octicons-react'
import {Box, Button, Octicon, sx, type SxProp, themeGet, useFocusZone} from '@primer/react'
import {addMonths, format, isAfter, isBefore, subMonths} from 'date-fns'
import styled from 'styled-components'
import {isMacOS} from '@github-ui/get-os'
import {testIdProps} from '@github-ui/test-id-props'
import {useShortcutCalculator} from '../hooks/index'
import {DateResources} from '../strings.en-us'
import {sanitizeDate} from '../utils/functions'
import {HiddenDescription} from './HiddenDescription'
import {DatePickerTextInput} from './Input'
import {Month} from './Month'
import {useDatePickerContext} from './Provider'
import type {Months} from '../types'

const months: Months[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const DatePickerPanelContainer = styled(Box)`
  align-items: stretch;
  background-color: ${themeGet('colors.canvas.default')};
  display: flex;
  flex-direction: column;
  position: relative;

  & *[disabled],
  & *[aria-disabled='true'] {
    pointer-events: none;
  }
`

const DatePickerTopNav = styled(Box)`
  display: flex;
  justify-content: space-between;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: ${themeGet('space.3')};
  z-index: 10;
`

const DatePickerPanelMonths = styled(Box)`
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  gap: ${themeGet('space.6')};
  margin-top: ${themeGet('space.1')};
  padding: ${themeGet('space.3')};
  position: relative;
`

const DatePickerPanelFooter = styled(Box)`
  border-top: 1px solid;
  border-top-color: ${themeGet('colors.border.default')};
  display: flex;
  align-items: stretch;
  flex-direction: column;
  padding: ${themeGet('space.2')} ${themeGet('space.3')};
  gap: ${themeGet('space.2')};
`

const FooterRow = styled(Box)`
  align-items: center;
  display: flex;
  flex-basis: 0;
  flex-direction: row;
  justify-content: space-between;
  position: relative;
  gap: ${themeGet('space.2')};
`

const ArrowButton = styled(Button)`
  width: 40px;
  height: 28px;
`

const Select = styled.select<SxProp>`
  background: ${themeGet('colors.canvas.default')};
  border: 0;
  color: ${themeGet('colors.fg.default')};
  font-weight: 600;
  border-radius: 6px;
  padding: ${themeGet('space.1')};
  ${sx};

  :focus {
    outline: none;
    box-shadow: 0 0 0 2px ${themeGet('colors.accent.fg')};
  }
`

const Option = styled.option<SxProp>`
  background: ${themeGet('colors.canvas.default')};
  border: 0;
  color: ${themeGet('colors.fg.default')};
  font-weight: 400;
  padding: ${themeGet('space.2')} ${themeGet('space.2')};
  ${sx};
`

const getDateElement = (panelRef: React.RefObject<HTMLDivElement>, date: Date) =>
  panelRef.current?.querySelector<HTMLElement>(`[data-date="${format(date, 'MM/dd/yyyy')}"]`) ?? undefined

const getFirstEnabledDate = (panelRef: React.RefObject<HTMLDivElement>) =>
  panelRef.current?.querySelector<HTMLElement>('[data-date][aria-disabled="false"]') ?? undefined

export const DatePickerPanel = () => {
  const {
    configuration: {
      minDate,
      maxDate,
      confirmation,
      view,
      showTodayButton: configShowTodayButton,
      showClearButton,
      compressedHeader,
      showInputs,
      variant,
    },
    isDirty,
    saveValue,
    currentViewingDate,
    goToMonth,
    onClearSelection,
    onDateHover,
    setFocusDate,
    inputRef,
    focusDate,
    activeRangeEnd,
  } = useDatePickerContext()
  const panelRef = useRef(null)
  const datePanelRef = useRef<HTMLDivElement>(null)

  const calculateShortcutDate = useShortcutCalculator()

  const getNextFocusable = useCallback(
    (_: Direction, from: Element | undefined, event: KeyboardEvent): HTMLElement | undefined => {
      const rawFromDate = from?.getAttribute('data-date')
      const fromDate = sanitizeDate(rawFromDate ? new Date(rawFromDate) : new Date())
      const newDate = calculateShortcutDate(fromDate, event) ?? fromDate
      setFocusDate(newDate)
      onDateHover(newDate)

      return getDateElement(panelRef, newDate)
    },
    [calculateShortcutDate, onDateHover, setFocusDate],
  )

  useFocusZone(
    {
      containerRef: datePanelRef,
      bindKeys: FocusKeys.ArrowAll | FocusKeys.HomeAndEnd | FocusKeys.PageUpDown,
      focusInStrategy: () => getDateElement(panelRef, focusDate) ?? getFirstEnabledDate(panelRef),
      getNextFocusable,
    },
    [getNextFocusable, focusDate],
  )

  // When changing current date, find and re-focus date that focus zone picked as selected.
  useEffect(() => {
    // This focuses on first day of the month, based on current implementation of focusInStrategy above.
    getFirstEnabledDate(panelRef)?.focus()
  }, [currentViewingDate])

  const previousDisabled = useMemo(() => {
    if (!minDate) return false

    const previous = subMonths(currentViewingDate, 1)
    return minDate.getFullYear() >= previous.getFullYear() && minDate.getMonth() > previous.getMonth()
  }, [minDate, currentViewingDate])

  const nextDisabled = useMemo(() => {
    if (!maxDate) return false

    const next = addMonths(currentViewingDate, view === '2-month' ? 2 : 1)
    return maxDate.getFullYear() <= next.getFullYear() && maxDate.getMonth() < next.getMonth()
  }, [maxDate, view, currentViewingDate])

  const showTodayButton = useMemo(() => {
    if (!configShowTodayButton) return false
    const today = sanitizeDate(new Date())
    if (minDate && isAfter(minDate, today)) return false
    if (maxDate && isBefore(maxDate, today)) return false
    return true
  }, [maxDate, minDate, configShowTodayButton])

  const showApplyButton = confirmation

  const currentMonth = useMemo(() => currentViewingDate.getMonth(), [currentViewingDate])
  const currentYear = useMemo(() => currentViewingDate.getFullYear(), [currentViewingDate])

  const headerSelectionHandler = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const int = parseInt(e.currentTarget.value, 10)
      if (e.currentTarget.id === 'picker-header-year') {
        goToMonth(new Date(int, currentMonth))
      } else {
        goToMonth(new Date(currentYear, int))
      }
    },
    [currentMonth, currentYear, goToMonth],
  )

  const getMonthPicker = useMemo(() => {
    let filteredMonths = months
    const monthElements: ReactNode[] = []
    if (minDate && currentYear === minDate.getFullYear()) {
      filteredMonths = filteredMonths.filter(month => months.indexOf(month) >= minDate.getMonth())
    }
    if (maxDate && currentYear === maxDate.getFullYear()) {
      filteredMonths = filteredMonths.filter(month => months.indexOf(month) <= maxDate.getMonth())
    }
    for (const month of filteredMonths) {
      monthElements.push(
        <Option key={month} value={months.indexOf(month)}>
          {month}
        </Option>,
      )
    }

    return (
      <Select
        id="picker-header-month"
        onChange={headerSelectionHandler}
        sx={{mr: '6px'}}
        value={currentMonth}
        aria-label="Go to month"
      >
        {monthElements}
      </Select>
    )
  }, [minDate, maxDate, currentMonth, currentYear, headerSelectionHandler])

  const getYearPicker = useMemo(() => {
    const years: ReactNode[] = []
    const todaysYear = sanitizeDate(new Date()).getFullYear()
    const minYear = minDate ? minDate.getFullYear() : todaysYear - 200
    const maxYear = maxDate ? maxDate.getFullYear() : todaysYear + 200
    for (let i = minYear; i <= maxYear; i++) {
      years.push(
        <Option key={i} value={i}>
          {i}
        </Option>,
      )
    }

    return (
      <Select id="picker-header-year" onChange={headerSelectionHandler} value={currentYear} aria-label="Go to year">
        {years}
      </Select>
    )
  }, [minDate, maxDate, currentYear, headerSelectionHandler])

  // The date calculation is cheap, but memoizing it preserves the date object reference
  // to avoid unnecessary re-rendering of the second month and its Day children.
  const secondMonthDate = useMemo(
    () => (view === '2-month' ? addMonths(currentViewingDate, 1) : null),
    [view, currentViewingDate],
  )

  const showButtons = showClearButton || showTodayButton || showApplyButton

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Submit on ctrl/cmd+enter, but not when variant is multi because ctrl/cmd+enter is there used to add multiple dates
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    const modifierKey = isMacOS() ? event.metaKey : event.ctrlKey
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (isDirty && modifierKey && event.key === 'Enter' && variant !== 'multi') {
      event.stopPropagation()
      saveValue()
    }
  }

  const goToToday = useCallback(() => {
    setFocusDate(sanitizeDate(new Date()))
  }, [setFocusDate])

  const clearDate = useCallback(() => {
    onClearSelection()
  }, [onClearSelection])

  const descriptionId = useId()

  const description =
    variant === 'single'
      ? DateResources.monthSingleDescription
      : variant === 'multi'
        ? DateResources.monthMultiDescription(isMacOS())
        : variant === 'range' && activeRangeEnd !== null
          ? DateResources.monthRangeDescription[activeRangeEnd]
          : ''

  return (
    <DatePickerPanelContainer ref={panelRef} onKeyDown={handleKeyDown} {...testIdProps('datepicker-panel')}>
      <DatePickerTopNav as="header">
        {compressedHeader && (
          <Box {...testIdProps('datepicker-compressed-header')} sx={{flex: 1}}>
            {getMonthPicker}
            {getYearPicker}
          </Box>
        )}
        <ArrowButton
          size="small"
          sx={{mr: 1}}
          onClick={() => goToMonth(subMonths(currentViewingDate, 1))}
          disabled={previousDisabled}
          aria-label="Go to previous month"
          {...testIdProps('previous-button')}
        >
          <Octicon icon={ChevronLeftIcon} sx={{color: 'fg.muted'}} />
        </ArrowButton>
        <ArrowButton
          size="small"
          onClick={() => goToMonth(addMonths(currentViewingDate, 1))}
          disabled={nextDisabled}
          aria-label="Go to next month"
          {...testIdProps('next-button')}
        >
          <Octicon icon={ChevronRightIcon} sx={{color: 'fg.muted'}} />
        </ArrowButton>
      </DatePickerTopNav>

      <DatePickerPanelMonths ref={datePanelRef}>
        <HiddenDescription id={descriptionId}>{description}</HiddenDescription>

        <Month date={currentViewingDate} aria-describedby={descriptionId} />
        {secondMonthDate && <Month date={secondMonthDate} aria-describedby={descriptionId} />}
      </DatePickerPanelMonths>

      {(showButtons || showInputs) && (
        <DatePickerPanelFooter as="footer">
          {(view === '1-month' || !showButtons || variant === 'multi') && showInputs && (
            <FooterRow>
              <DatePickerTextInput ref={inputRef} fullWidth />
            </FooterRow>
          )}
          {showButtons && (
            <FooterRow>
              <Box sx={{display: 'flex'}}>
                {showClearButton && (
                  <Button
                    variant="invisible"
                    size="small"
                    onClick={clearDate}
                    aria-label="Clear selected date"
                    disabled={!currentViewingDate}
                  >
                    Clear
                  </Button>
                )}
                {showTodayButton && (
                  <Button
                    variant="invisible"
                    size="small"
                    onClick={goToToday}
                    aria-label="Go to today's date"
                    disabled={
                      currentViewingDate.getFullYear() === new Date().getFullYear() &&
                      currentViewingDate.getMonth() === new Date().getMonth()
                    }
                  >
                    Today
                  </Button>
                )}
              </Box>
              {view === '2-month' && showInputs && variant !== 'multi' && <DatePickerTextInput ref={inputRef} />}
              {showApplyButton && (
                <Button
                  variant="primary"
                  size="small"
                  disabled={!isDirty}
                  onClick={() => saveValue()}
                  aria-label="Apply selection and close"
                  type="submit"
                  {...testIdProps('datepicker-apply')}
                >
                  Apply
                </Button>
              )}
            </FooterRow>
          )}
        </DatePickerPanelFooter>
      )}
    </DatePickerPanelContainer>
  )
}

try{ DatePickerPanelContainer.displayName ||= 'DatePickerPanelContainer' } catch {}
try{ DatePickerTopNav.displayName ||= 'DatePickerTopNav' } catch {}
try{ DatePickerPanelMonths.displayName ||= 'DatePickerPanelMonths' } catch {}
try{ DatePickerPanelFooter.displayName ||= 'DatePickerPanelFooter' } catch {}
try{ FooterRow.displayName ||= 'FooterRow' } catch {}
try{ ArrowButton.displayName ||= 'ArrowButton' } catch {}
try{ DatePickerPanel.displayName ||= 'DatePickerPanel' } catch {}